import { Injectable } from '@angular/core'
import {
  ClientIPList,
  GoalInfo,
  IndividualPlanInfo,
  ProgramEnrollmentInfo,
  StepInfo,
} from '../../../../../../models/ClientIPList'
import { TRANSLATIONS } from 'src/assets/i18n/cs'
import { ClientIPTreeCategory, ClientIPTreeNode } from '../../types/ClientIPListTree'
import { DateUtils } from 'src/app/services/date-utils.service'
import { DefaultClientIPListIcons } from '../../types/ClientIPListIcons'
import { TranslocoService } from '@jsverse/transloco'

@Injectable({
  providedIn: 'root',
})
export class ClientIpListTreeNodesTransformService {
  protected readonly TRANSLATIONS = TRANSLATIONS.CLIENT_IP_LIST.TREE
  constructor(private transloco: TranslocoService) {}

  transformToTreeNodes(clientIPList: ClientIPList[]): ClientIPTreeNode[] {
    return clientIPList.flatMap((item) =>
      item.programEnrollment.map((program) => this.transformProgramEnrollment(program)),
    )
  }

  private transformProgramEnrollment(program: ProgramEnrollmentInfo): ClientIPTreeNode {
    const programName = program.programName
    const node: ClientIPTreeNode = {
      label: programName,
      data: {
        id: program.id,
        programId: program.programId,
        dueDate: program.dueDate,
        isCompleted: program.isArchived,
        isTerminated: program.isTerminated,
        ionIcon: program.isArchived
          ? 'archive-outline'
          : program.isTerminated
            ? 'close-circle-outline'
            : DefaultClientIPListIcons.PROGRAM_ENROLLMENT,
        iconTooltip: program.isArchived
          ? this.TRANSLATIONS.COMPLETED.PROGRAM_ARCHIVED_TOOLTIP +
            ' ' +
            DateUtils.getDateInHumanReadable(program.archivedAt)
          : program.isTerminated
            ? this.TRANSLATIONS.COMPLETED.PROGRAM_ENROLLMENT_TERMINATED_TOOLTIP
            : this.transloco.translate('CLIENT_IP_CARD.ITEM_TYPE.PROGRAM_ENROLLMENT'),
        importantIcon: program.isArchived || program.isTerminated,
        type: ClientIPTreeCategory.PROGRAM_ENROLLMENT,
        displayPlusButton: true,
        plusButtonTooltip: this.TRANSLATIONS.ADD.INDIVIDUAL_PLAN_ADD_TOOLTIP,
        programName, // Store program name for child editors
      },
    }

    if (program.individualPlans && program.individualPlans.length > 0) {
      node.children = program.individualPlans.map((plan) => this.transformIndividualPlan(plan, programName))
    }

    return node
  }

  private transformIndividualPlan(plan: IndividualPlanInfo, programName: string): ClientIPTreeNode {
    const node: ClientIPTreeNode = {
      label: plan.name,
      data: {
        id: plan.id,
        isCompleted: plan.isClosed,
        ionIcon: plan.isClosed ? 'archive-outline' : DefaultClientIPListIcons.INDIVIDUAL_PLAN,
        iconTooltip: plan.isClosed
          ? this.TRANSLATIONS.COMPLETED.INDIVIDUAL_PLAN_CLOSED_TOOLTIP +
            ' ' +
            DateUtils.getDateInHumanReadable(plan.closedAt)
          : this.transloco.translate('CLIENT_IP_CARD.ITEM_TYPE.INDIVIDUAL_PLAN'),
        importantIcon: plan.isClosed,
        dueDate: plan.dueDate,
        type: ClientIPTreeCategory.INDIVIDUAL_PLAN,
        displayPlusButton: true,
        plusButtonTooltip: this.TRANSLATIONS.ADD.GOAL_ADD_TOOLTIP,
        programName, // Pass down program name
      },
    }

    if (plan.goals && plan.goals.length > 0) {
      node.children = plan.goals.map((goal) => this.transformGoal(goal, programName))
    }

    return node
  }

  private transformGoal(goal: GoalInfo, programName: string): ClientIPTreeNode {
    const node: ClientIPTreeNode = {
      label: goal.name,
      data: {
        id: goal.id,
        isCompleted: goal.isCompleted,
        dueDate: goal.dueDate,
        ionIcon: goal.isCompleted ? 'checkmark-circle-outline' : DefaultClientIPListIcons.GOAL,
        iconTooltip: goal.isCompleted
          ? this.TRANSLATIONS.COMPLETED.GOAL_COMPLETED_TOOLTIP +
            ' ' +
            DateUtils.getDateInHumanReadable(goal.completedAt)
          : this.transloco.translate('CLIENT_IP_CARD.ITEM_TYPE.GOAL'),
        importantIcon: goal.isCompleted,
        type: ClientIPTreeCategory.GOAL,
        displayPlusButton: true,
        plusButtonTooltip: this.TRANSLATIONS.ADD.STEP_ADD_TOOLTIP,
        programName, // Pass down program name
      },
    }

    if (goal.steps && goal.steps.length > 0) {
      node.children = goal.steps.map((step) => this.transformStep(step, programName))
    }

    return node
  }

  private transformStep(step: StepInfo, programName: string): ClientIPTreeNode {
    return {
      label: step.name,
      data: {
        id: step.id,
        isCompleted: step.isCompleted,
        dueDate: step.dueDate,
        ionIcon: step.isCompleted ? 'checkmark-circle-outline' : DefaultClientIPListIcons.STEP,
        iconTooltip: step.isCompleted
          ? this.TRANSLATIONS.COMPLETED.STEP_COMPLETED_TOOLTIP +
            ' ' +
            DateUtils.getDateInHumanReadable(step.completedAt)
          : this.transloco.translate('CLIENT_IP_CARD.ITEM_TYPE.STEP'),
        importantIcon: step.isCompleted,
        type: ClientIPTreeCategory.STEP,
        programName, // Store program name for step editor
      },
    }
  }
}
